import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import {
  hierarchyNodeAt,
  hierarchyOptions,
  sectionAt,
  type ChallengeResponse,
  type ConfigResponse,
  type GuessRequest,
  type HintRequest,
  type RevealResponse,
  type SectionNode,
} from "@versedrill/core";
import { ApiError, api, type GameSource } from "./api.ts";
import { useRetryWhenOnline } from "./useInstall.ts";
import { initialState, newLevel, reduce, stars as starsOf, type Choice, type GameState, type Level } from "./game.ts";
import { useStore } from "./store.ts";

export interface GameResult extends RevealResponse {
  stars: number;
  score: number;
  possible: number;
}

const prefersReducedMotion = () => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
const pause = (ms: number) => new Promise<void>((r) => setTimeout(r, prefersReducedMotion() ? 0 : ms));
const describeError = (e: unknown) => (e instanceof ApiError ? e.message : "Something went wrong. Please try again.");

const hierarchyChoices = (nodes: readonly { name: string; description?: string }[]): Choice[] => nodes.map((n) => ({ title: n.name, description: n.description }));
/** "3:1–4:6", "3:1–7", or "3:1" — the book is already known in Round 2, so it's left out. */
export function rangeText(n: Pick<SectionNode, "start" | "end">): string {
  const { start: a, end: b } = n;
  if (a.chapter !== b.chapter) return `${a.chapter}:${a.verse}–${b.chapter}:${b.verse}`;
  return a.verse === b.verse ? `${a.chapter}:${a.verse}` : `${a.chapter}:${a.verse}–${b.verse}`;
}
const sectionChoices = (nodes: readonly SectionNode[]): Choice[] => nodes.map((n) => ({ title: n.name, description: n.description, meta: rangeText(n) }));

/**
 * Play one game against `source`. `onFinished` is called exactly once, when the reveal is in and the score is final;
 * the caller decides what finishing means (record a daily result, report a solved sent verse…).
 */
export function useGame(source: GameSource, onFinished: (result: GameResult) => void) {
  const [state, dispatch] = useReducer(reduce, undefined, initialState);
  const [challenge, setChallenge] = useState<ChallengeResponse | null>(null);
  const [config, setConfig] = useState<ConfigResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadStatus, setLoadStatus] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [unredacted, setUnredacted] = useState<string | null>(null);
  const [pendingReveal, setPendingReveal] = useState<RevealResponse | null>(null);
  const [result, setResult] = useState<GameResult | null>(null);
  const [announcement, setAnnouncement] = useState("");

  // Latest values for use inside async chains that outlive a render.
  const stateRef = useRef<GameState>(state);
  stateRef.current = state;
  const treeRef = useRef<SectionNode[]>([]);
  const finishedRef = useRef(onFinished);
  finishedRef.current = onFinished;
  const unredactedRef = useRef(unredacted);
  unredactedRef.current = unredacted;
  const resultRef = useRef(result);
  resultRef.current = result;
  /** The slot the player placed the verse in, so a solved game can fetch the reveal again in another version. */
  const slotRef = useRef<number | undefined>(undefined);

  // ---- load --------------------------------------------------------------------------------
  const load = useCallback(() => {
    let cancelled = false;
    setLoadError(null);
    setLoadStatus(null);
    Promise.all([source.challenge(), api.config()])
      .then(([c, cfg]) => {
        if (cancelled) return;
        setChallenge(c);
        setConfig(cfg);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setLoadError(describeError(e));
        setLoadStatus(e instanceof ApiError ? e.status : null);
      });
    return () => {
      cancelled = true;
    };
  }, [source]);
  useEffect(() => load(), [load]);
  // A game that failed to load because the connection dropped starts itself again when it returns.
  useRetryWhenOnline(loadError !== null, load);

  // ---- changing Bible version mid-game --------------------------------------------------------
  // The game's structure is identical in every version, so switching only swaps the words on screen: the verse, the real
  // names if they were revealed, Round 3's neighbours if the player is in that round, and the solved text if they've
  // finished. Nothing about the board, the guesses or the score is touched.
  const translation = useStore((s) => s.translation);
  const shownVersion = useRef(translation);
  useEffect(() => {
    if (shownVersion.current === translation) return;
    shownVersion.current = translation;
    let cancelled = false;
    void (async () => {
      try {
        const s = stateRef.current;
        const [c, real, r3, rev] = await Promise.all([
          source.challenge(),
          unredactedRef.current !== null ? source.unredacted() : Promise.resolve(null),
          s.slotVerses.length > 0 ? source.round3(s.bookPath, s.sectionPath) : Promise.resolve(null),
          resultRef.current ? source.reveal(s.bookPath, s.sectionPath, slotRef.current) : Promise.resolve(null),
        ]);
        if (cancelled) return;
        setChallenge(c);
        if (real) setUnredacted(real.text);
        if (r3) dispatch({ type: "slotVerses", verses: r3.verses });
        if (rev) setResult((prev) => (prev ? { ...prev, text: rev.text } : prev));
      } catch (e) {
        if (!cancelled) setError(describeError(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [translation, source]);

  const start = useCallback(() => {
    if (!challenge || !config) return;
    const options = hierarchyOptions([], config.hierarchy) ?? [];
    dispatch({ type: "begin", hints: challenge.hints, redactable: challenge.redactable, first: newLevel(1, [], hierarchyChoices(options)) });
  }, [challenge, config]);

  // ---- finishing: once the reveal is in, score is final ------------------------------------
  useEffect(() => {
    if (state.phase !== "finishing" || !pendingReveal) return;
    const final = starsOf(state);
    const full: GameResult = { ...pendingReveal, stars: final, score: state.score, possible: state.possible };
    setResult(full);
    dispatch({ type: "finished" });
    finishedRef.current(full);
  }, [state, pendingReveal]);

  // ---- moving between levels and rounds ----------------------------------------------------
  const finalize = useCallback(
    async (slot: number | undefined) => {
      dispatch({ type: "finishing" });
      slotRef.current = slot;
      const s = stateRef.current;
      setPendingReveal(await source.reveal(s.bookPath, s.sectionPath, slot));
    },
    [source],
  );

  const advance = useCallback(
    async (level: Level, index: number, done: boolean) => {
      if (!config) return;
      const path = [...level.prefix, index];

      if (level.round === 1) {
        if (!done) {
          const next = hierarchyOptions(path, config.hierarchy) ?? [];
          dispatch({ type: "append", level: newLevel(1, path, hierarchyChoices(next)) });
          return;
        }
        const bookId = hierarchyNodeAt(path, config.hierarchy)?.bookId;
        if (bookId === undefined) throw new Error("Round 1 finished on a node that isn't a book");
        const sections = await api.bookSections(bookId);
        treeRef.current = sections.tree;
        dispatch({ type: "book", bookId, bookName: sections.name, bookPath: path });
        dispatch({ type: "append", level: newLevel(2, [], sectionChoices(sections.tree)) });
        return;
      }

      if (level.round === 2) {
        if (!done) {
          const children = sectionAt(treeRef.current, path)?.children ?? [];
          dispatch({ type: "append", level: newLevel(2, path, sectionChoices(children)) });
          return;
        }
        dispatch({ type: "section", sectionPath: path });
        const r3 = await source.round3(stateRef.current.bookPath, path);
        if (r3.verses.length === 0) {
          // The section is the passage itself: nothing left to place.
          await finalize(undefined);
          return;
        }
        dispatch({ type: "slotVerses", verses: r3.verses });
        dispatch({ type: "append", level: newLevel(3, [], Array.from({ length: r3.slots }, (_, i) => ({ title: `Position ${i + 1}` }))) });
        return;
      }

      await finalize(index);
    },
    [config, source, finalize],
  );

  // ---- player actions ----------------------------------------------------------------------
  const guessBody = (level: Level, index: number): GuessRequest => {
    const s = stateRef.current;
    if (level.round === 1) return { round: 1, prefix: level.prefix, index };
    if (level.round === 2) return { round: 2, bookPath: s.bookPath, prefix: level.prefix, index };
    return { round: 3, bookPath: s.bookPath, sectionPath: s.sectionPath, index };
  };

  const pick = useCallback(
    async (levelIndex: number, index: number) => {
      const level = stateRef.current.levels[levelIndex];
      if (busy || !level || level.correct !== null || index in level.wrong || stateRef.current.phase !== "playing") return;
      setBusy(true);
      setError(null);
      try {
        const r = await source.guess(guessBody(level, index));
        if (!r.correct) {
          dispatch({ type: "wrong", level: levelIndex, index, direction: r.direction ?? "below" });
          setAnnouncement(`Not quite. Look ${r.direction === "above" ? "above" : "below"}.`);
        } else {
          dispatch({ type: "correct", level: levelIndex, index });
          setAnnouncement("Correct.");
          await pause(520);
          await advance(level, index, Boolean(r.done));
        }
      } catch (e) {
        setError(describeError(e));
      } finally {
        setBusy(false);
      }
    },
    [busy, source, advance],
  );

  const hint = useCallback(async () => {
    const s = stateRef.current;
    const li = s.levels.findIndex((l) => l.correct === null);
    const level = s.levels[li];
    if (busy || !level || s.hintsLeft <= 0) return;
    setBusy(true);
    setError(null);
    try {
      const guessed = Object.keys(level.wrong).map(Number);
      const body: HintRequest =
        level.round === 1
          ? { round: 1, prefix: level.prefix, guessed }
          : level.round === 2
            ? { round: 2, bookPath: s.bookPath, prefix: level.prefix, guessed }
            : { round: 3, bookPath: s.bookPath, sectionPath: s.sectionPath, guessed };
      const r = await source.hint(body);
      dispatch({ type: "hint", level: li, highlighted: r.highlighted });
      setAnnouncement("Hint: the answer is one of the highlighted options.");
    } catch (e) {
      setError(describeError(e));
    } finally {
      setBusy(false);
    }
  }, [busy, source]);

  const revealNames = useCallback(async () => {
    if (!challenge?.redactable || !stateRef.current.redactionOn) return;
    try {
      setUnredacted((await source.unredacted()).text);
      dispatch({ type: "reveal" });
    } catch (e) {
      setError(describeError(e));
    }
  }, [challenge, source]);

  return {
    challenge,
    config,
    state,
    result,
    busy,
    error,
    loadError,
    loadStatus,
    announcement,
    unredacted,
    start,
    pick,
    hint,
    revealNames,
    reload: load,
    dismissError: () => setError(null),
  };
}
